import { ALL_LANGUAGES_TYPE } from '../i18n/constants'
import {
    appLangFromStr,
    uniqStrings,
} from '../i18n/useTranslation'
import { info, logException } from '../services/sentryLogger'
import { defaultParamsFitme } from './defaultParamsFitme'
import { ParamsDTO, PartialParameters } from './types'
import { p2b, p2bin, p2col, p2i } from './utils'

/**
    * Cf. https://docs.google.com/document/d/1hoKS3rpL8aqIKk_mtB_vseWcO3QN6SI0Mg_Yejt1QBM/edit?tab=t.0
    * https://docs.google.com/document/d/1lh9gcKAXL3SuWTQOasJzgCxKATPR9gX4/edit
    * https://docs.google.com/presentation/d/1a1Pwh8_5zakWZ0CCwezXvyKAmGSbsXkD/edit#slide=id.p12 vs
    // normally '05' is the default color => branding params...
    // '10' for the new talent pool screen, add new screen, Y|N
    * FIXME https://docs.google.com/document/d/1SBu0vJDcUjcKqh2CipBinp-a_ncws3hD/edit
    // Parameter 001  = Version of UI = 0 = Classical | 1 = 360 | 2 = TalentPool 
    // Parameter 002 = Intro Screen (HasIntro)
    // Parameter 003 = Brand Screen#2(HasBranding)
    // Parameter 002 = Type of 360: 0 (leader) | 1 (env) | 2 (both)
    // Parameter 002 = Case of TalentPool: 0 | 1
*/

export enum UI_ID {
    LEGACY = 1,
    NEW_UI = 2,
    TALENTPOOL = 3,
    SCANNING360 = 4,
    QSORTV2 = 5,
}

export enum UI_TYPE {
    VALUEFIT42 = 'ValueFit42',
    SCANNING360 = '360',
}

/**
 *
 * @param paramsDTO
 * @param slug
 * @param source
 * @returns
 */
export const convertServerParams = (
    paramsDTO: Partial<ParamsDTO>
): PartialParameters => {
    info('convertServerParams', paramsDTO)
    // Attention:
    // /!\ '' ?? '1' => ''
    // /!\ '' || '1' => '1'
    const inp =
        paramsDTO.assessmentParams ?? ({} as ParamsDTO['assessmentParams'])

    const availableLanguages = paramsDTO.brandLanguages?.length
        ? (uniqStrings(
              paramsDTO.brandLanguages
                  .filter((l) => l.isActive === '1')
                  .map(({ locale }) => appLangFromStr(locale))
          ) as ALL_LANGUAGES_TYPE[])
        : defaultParamsFitme.brandParams.availableLanguages

    const out = {
        ...defaultParamsFitme.assessmentParams,
    } as PartialParameters['assessmentParams']

    const outBrand = {
        ...defaultParamsFitme.brandParams,
        ...paramsDTO.brandParams,
        availableLanguages,
    } as PartialParameters['brandParams']

    if (paramsDTO.ui?.type === UI_TYPE.VALUEFIT42) {
        /* Common part */
        p2b(inp?.['01'], out, 'hasIntro')
        p2b(inp?.['02'], out, 'has2ndBranding')
        p2b(inp?.['03'], out, 'hasHowTo') // common with QSORTV2 and ValueFit42
        p2bin(inp?.['05'], out, 'values_with_icons')
        p2b(inp?.['07'], out, 'hasAwardRound')
        p2bin(inp?.['10'], out, 'download_report')
        outBrand.returnUrl =
            inp?.['11'] || defaultParamsFitme.brandParams.returnUrl
        p2b(inp?.['12'], out, 'hasPower')
        out.hasPower = defaultParamsFitme.assessmentParams.hasPower // FIXME force (phase 3)

        // ii. Brand
        p2col(inp?.['13'], outBrand, 'backgroundColor')
        p2col(inp?.['14'], outBrand, 'primaryColor')
        p2col(inp?.['15'], outBrand, 'secondaryColor')
        p2col(inp?.['16'], outBrand, 'textColor')
        p2bin(inp?.['20'], out, 'debug_mode')

        if (paramsDTO?.ui?.id === UI_ID.NEW_UI) {
            p2bin(inp?.['04'], out, 'submit_confirmation')
            p2i(inp?.['06'], out, 'delayedChoiceDuration')
            p2b(inp?.['08'], out, 'hasConfirmRound')
            p2b(inp?.['09'], out, 'hasHowToBuckets')
            out.simple_progress_bar = inp?.['12'] === '1'
        } else if (paramsDTO?.ui?.id === UI_ID.QSORTV2) {
            // Cf. https://docs.google.com/document/d/1hoKS3rpL8aqIKk_mtB_vseWcO3QN6SI0Mg_Yejt1QBM/edit?tab=t.0
            out.isQSortV2 = true

            // i. UX
            p2i(inp?.['04'], out, 'nSets') // FIXME NEW_UI?
            p2i(inp?.['06'], out, 'nBucket') // FIXME NEW_UI?

            // ii. QSortV2 specific
            p2i(inp?.['17'], out, 'reflectionMode') // 0 = OFF, 1 = yes/no, 2 = yes/no + sort
            p2b(inp?.['18'], out, 'promptRankMidBucket')
            out.stmsPerScreen =
                inp?.['19'] || defaultParamsFitme.assessmentParams.stmsPerScreen
            console.debug('>>>> QSortV2 PARAMS', { inp, out })
        } else if (paramsDTO?.ui?.id === UI_ID.LEGACY) {
            logException('LEGACY UI', 'Server error redirecting to new UI?')
        }
    } else if (paramsDTO.ui?.type === UI_TYPE.SCANNING360) {
        out.is360 = true
        // p2i(inp?.['01'], out, 'uiType') // FIXME for later usage
        p2b(inp?.['02'], out, 'is360WithLeadership')
        p2b(inp?.['03'], out, 'is360WithWorkenv')
        p2bin(inp?.['20'], out, 'debug_mode')
    } else {
        logException(
            'convertServerParams',
            'Unknown UI type:' + paramsDTO.ui?.type + ' id: ' + paramsDTO.ui?.id
        )
    }

    const fullConverted = {
        brandParams: {
            ...outBrand,
        },
        assessmentParams: {
            ...out,
        },
    }
    console.debug('convertServerParams', { fullConverted })
    return fullConverted
}
